import type { AIAssistantRecentItem } from '../../components/AIAssistant.js';
import type { AssistantActionBase, AssistantMessage } from '../../types/index.js';
import type { IntellioAssistantConversationSessionRecord } from '@triedge/domain-core';

const normalizeRecentText = (value: unknown): string =>
  String(value ?? '').replace(/\s+/g, ' ').trim();

const formatRecentSessionSubtitle = (
  session: IntellioAssistantConversationSessionRecord,
  routeLabel: string,
): string => {
  const label = normalizeRecentText(session.routeLabel) || routeLabel;
  const count = Number(session.messageCount ?? 0);
  const messageLabel = `${count} message${count === 1 ? '' : 's'}`;
  return `${label} · ${messageLabel}`;
};

export const projectAssistantConversationRecentItems = <
  TCitation,
  TAction extends AssistantActionBase,
>(
  messages: readonly AssistantMessage<TCitation, TAction>[],
  routeLabel: string,
  options: {
    persistedSessions?: readonly IntellioAssistantConversationSessionRecord[];
    limit?: number;
  } = {},
): AIAssistantRecentItem[] => {
  const limit = options.limit ?? 5;
  const seenPrompts = new Set<string>();
  const recentItems: AIAssistantRecentItem[] = [];

  const sessions = [...(options.persistedSessions ?? [])]
    .filter((session) => Number(session.messageCount ?? 0) > 0)
    .sort((left, right) => Number(right.updatedAt ?? 0) - Number(left.updatedAt ?? 0));

  for (const session of sessions) {
    const prompt = normalizeRecentText(session.firstUserPreview)
      || normalizeRecentText(session.title)
      || normalizeRecentText(session.lastMessagePreview);
    if (!prompt || seenPrompts.has(prompt.toLowerCase())) {
      continue;
    }
    seenPrompts.add(prompt.toLowerCase());
    recentItems.push({
      id: `session-${session.id}`,
      title: prompt,
      subtitle: formatRecentSessionSubtitle(session, routeLabel),
      prompt,
      sessionId: session.id,
      previewMessageId: session.firstUserMessageId ?? session.lastMessageId ?? undefined,
      source: 'persisted-session',
    });
    if (recentItems.length >= limit) {
      return recentItems;
    }
  }

  for (const message of [...messages].reverse()) {
    if (message.role !== 'user') {
      continue;
    }
    const prompt = normalizeRecentText(message.content);
    if (!prompt || seenPrompts.has(prompt.toLowerCase())) {
      continue;
    }
    seenPrompts.add(prompt.toLowerCase());
    recentItems.push({
      id: message.messageId ?? `recent-local-${recentItems.length + 1}`,
      title: prompt,
      subtitle: routeLabel,
      prompt,
      previewMessageId: message.messageId,
      source: 'current-session',
    });
    if (recentItems.length >= limit) {
      return recentItems;
    }
  }

  return recentItems;
};
